import { useMemo, useState } from 'react';
import { Check, MagnifyingGlass } from '@phosphor-icons/react';
import Sheet from '../Sheet';
import { useToast } from '../../hooks/useToast';
import { confirmTimeZone } from '../../services/liveCallBookings';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { deviceTimeZone, TIME_ZONE_GROUPS, timeZoneLabel, type TimeZoneOption } from '../../lib/timeZones';

const ROW_TEXT = 'text-[17px] leading-[20px] tracking-[-0.43px] text-db-text-1';
const HEADER = 'px-4 pb-2 pt-6 text-[13px] leading-[18px] text-db-text-2';

/** A zone matches by its label ("Eastern Time - New York") or its id, at the start of a word. */
function matcher(query: string): (z: TimeZoneOption) => boolean {
  const q = query.trim().toLowerCase();
  if (!q) return () => true;
  const at = new RegExp(`(^|[^a-z0-9])${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  return (z) => at.test(z.label.toLowerCase()) || at.test(z.id.toLowerCase().replace(/_/g, ' '));
}

/**
 * The time zone sheet, the app's own: a search field and every zone from
 * lib/timeZones.ts grouped by offset, the device's zone first. A tap
 * applies at once and closes; the save (confirmLiveTimeZone) runs behind,
 * and if it fails the zone before is put back. Availability, the booking
 * screen and the request screens all open this one.
 */
export function ZoneSheet({ zone, onSaved, onClose, onCommitted }: {
  zone: string;
  /** Called at once with the zone picked, and again with the zone before if the save fails. */
  onSaved: (zone: string) => void;
  onClose: () => void;
  /** Once the save has landed, for a page that reads again in the new zone. */
  onCommitted?: (zone: string) => void;
}) {
  const { showError } = useToast();
  const [query, setQuery] = useState('');
  const device = useMemo(() => deviceTimeZone(), []);
  const match = useMemo(() => matcher(query), [query]);
  const deviceOption = { id: device, label: timeZoneLabel(device), offsetMinutes: 0 };
  const groups = useMemo(() => TIME_ZONE_GROUPS
    .map((g) => ({ ...g, zones: g.zones.filter((z) => z.id !== device && match(z)) }))
    .filter((g) => g.zones.length), [device, match]);
  const showDevice = match(deviceOption);

  // Tapping the zone already shown still saves it: the first booking confirms the zone this way.
  const pick = (z: string) => {
    onClose();
    const before = zone;
    onSaved(z);
    confirmTimeZone(z).then(() => onCommitted?.(z), (err) => {
      if (z !== before) onSaved(before);
      showError(liveLessonsErrorMessage(err, 'Could not save your time zone. Please try again.'));
    });
  };

  const row = (z: TimeZoneOption, first: boolean) => (
    <li key={z.id} role="none" className="pl-4">
      <button type="button" role="option" aria-selected={z.id === zone} onClick={() => pick(z.id)}
        className={
          'ios-press flex min-h-[52px] w-full cursor-pointer items-center gap-3 py-2 pr-4 text-left ' +
          'focus:outline-none focus-visible:bg-[rgba(120,120,128,0.08)] hover:bg-[rgba(120,120,128,0.06)] ' +
          (first ? '' : 'border-t border-[#e6e6e6]')
        }>
        <span className={`${ROW_TEXT} min-w-0 flex-1`}>{z.label}</span>
        {z.id === zone && <Check size={18} weight="bold" className="flex-shrink-0 text-biscayne" aria-hidden="true" />}
      </button>
    </li>
  );

  return (
    <Sheet title="Time Zone" onClose={onClose}>
      <div className="sticky top-0 z-10 bg-db-bg pb-1">
        <label className="flex h-[44px] items-center gap-2 rounded-[12px] bg-[rgba(120,120,128,0.12)] px-3">
          <MagnifyingGlass size={18} className="flex-shrink-0 text-db-text-2" aria-hidden="true" />
          <input type="search" value={query} autoFocus onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for a city or time zone" aria-label="Search time zones"
            className="min-w-0 flex-1 bg-transparent text-[17px] leading-[22px] tracking-[-0.43px] text-db-text-1 placeholder:text-db-text-3 focus:outline-none" />
        </label>
      </div>
      <div className="pb-2">
        {showDevice && (
          <>
            <p className={HEADER}>This device</p>
            <ul role="listbox" aria-label="This device" className="overflow-hidden rounded-[26px] bg-white">{row(deviceOption, true)}</ul>
          </>
        )}
        {groups.map((g) => (
          <div key={g.offsetMinutes}>
            <p className={HEADER}>{g.label}</p>
            <ul role="listbox" aria-label={g.label} className="overflow-hidden rounded-[26px] bg-white">{g.zones.map((z, i) => row(z, i === 0))}</ul>
          </div>
        ))}
        {!showDevice && !groups.length && (
          <p className="px-4 pt-6 text-center text-[15px] leading-[20px] text-db-text-2">No time zone matches.</p>
        )}
      </div>
      <p className="px-4 pb-2 pt-3 text-[13px] leading-[18px] text-db-text-2">Lesson times and reminders use this time zone.</p>
    </Sheet>
  );
}
